import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #mediaQuery = window.matchMedia('(max-width: 749px)');

  #lastPerRow = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#mediaQuery.addEventListener('change', this.#handleMediaChange);

    this.current = 0;
    this.#updateArrowsVisibility();
    this.play();
  }

  #updateArrowsVisibility() {
    const arrows = this.querySelector('slideshow-arrows');
    if (!arrows) return;
    arrows.hidden = this.maxPage <= 0;
  }

  disconnectedCallback() {
    this.#mediaQuery.removeEventListener('change', this.#handleMediaChange);
    super.disconnectedCallback?.();
  }

  #handleMediaChange = () => {
    const perRow = this.slidesPerRow;
    if (this.#lastPerRow !== null && this.#lastPerRow !== perRow) {
      this.current = this.#current;
    }
    this.#lastPerRow = perRow;
    this.#updateArrowsVisibility();
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get slidesPerRow() {
    if (this.#mediaQuery.matches) return 1;
    const value = parseInt(this.getAttribute('data-slides-per-row'), 10);
    return Number.isNaN(value) || value < 1 ? 1 : Math.min(value, 3);
  }

  get maxPage() {
    const slides = this.refs.slides ?? [];
    const perRow = this.slidesPerRow;
    return Math.max(0, Math.ceil(slides.length / perRow) - 1);
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const perRow = this.slidesPerRow;
    const maxPage = this.maxPage;
    const pageCount = maxPage + 1;

    const wrapped =
      pageCount > 0 ? ((current % pageCount) + pageCount) % pageCount : 0;
    this.#current = wrapped;

    const start = wrapped * perRow;
    const end = Math.min(start + perRow, slides.length);

    const orderedIndices = [];
    for (let i = start; i < end; i++) orderedIndices.push(i);
    if (perRow > 1 && orderedIndices.length < perRow) {
      for (let i = 0; orderedIndices.length < perRow; i++) {
        orderedIndices.push(i % slides.length);
      }
    }

    this.#lastPerRow = perRow;

    slides.forEach((slide, index) => {
      const position = orderedIndices.indexOf(index);
      const visible = position !== -1;
      slide.setAttribute('aria-hidden', `${!visible}`);
      slide.style.order = visible ? String(position) : '';
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
